function regularPolyPoints(sides, cx, cy, r) {
  const points = [];
  for (let i = 0; i < sides; i++) {
    const a = ((i * 360) / sides - 90) * (Math.PI / 180);
    points.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return points;
}

const STAR_POINTS = [
  { x: 50, y: 0 },
  { x: 61, y: 35 },
  { x: 98, y: 35 },
  { x: 68, y: 57 },
  { x: 79, y: 92 },
  { x: 50, y: 72 },
  { x: 21, y: 92 },
  { x: 32, y: 57 },
  { x: 2, y: 35 },
  { x: 39, y: 35 },
];

/** Konva scene-node specs keyed by sidebar `shapeKey`. */
export const KONVA_SHAPE_SPECS = {
  rect: { fabricType: 'rect', width: 120, height: 80 },
  roundedRect: { fabricType: 'rect', width: 120, height: 80, rx: 12 },
  circle: { fabricType: 'circle', width: 100, height: 100, radius: 50 },
  ellipse: { fabricType: 'ellipse', width: 120, height: 80 },
  triangle: { fabricType: 'triangle', width: 100, height: 90 },
  rightTriangle: { fabricType: 'triangle', width: 100, height: 90 },
  line: { fabricType: 'line', width: 200, height: 3, strokeWidth: 3, fill: 'transparent' },
  dottedLine: {
    fabricType: 'line',
    width: 200,
    height: 3,
    strokeWidth: 3,
    strokeDashArray: [10, 8],
    fill: 'transparent',
  },
  star: { fabricType: 'polygon', width: 100, height: 92, points: STAR_POINTS },
  pentagon: { fabricType: 'polygon', width: 100, height: 100, points: regularPolyPoints(5, 50, 50, 45) },
  hexagon: { fabricType: 'polygon', width: 100, height: 100, points: regularPolyPoints(6, 50, 50, 45) },
  octagon: { fabricType: 'polygon', width: 100, height: 100, points: regularPolyPoints(8, 50, 50, 45) },
  diamond: {
    fabricType: 'polygon',
    width: 100,
    height: 100,
    points: [
      { x: 50, y: 0 },
      { x: 100, y: 50 },
      { x: 50, y: 100 },
      { x: 0, y: 50 },
    ],
  },
  trapezoid: {
    fabricType: 'polygon',
    width: 100,
    height: 80,
    points: [
      { x: 20, y: 0 },
      { x: 80, y: 0 },
      { x: 100, y: 80 },
      { x: 0, y: 80 },
    ],
  },
  parallelogram: {
    fabricType: 'polygon',
    width: 130,
    height: 70,
    points: [
      { x: 30, y: 0 },
      { x: 130, y: 0 },
      { x: 100, y: 70 },
      { x: 0, y: 70 },
    ],
  },
  cross: {
    fabricType: 'polygon',
    width: 80,
    height: 80,
    points: [
      { x: 28, y: 0 },
      { x: 52, y: 0 },
      { x: 52, y: 28 },
      { x: 80, y: 28 },
      { x: 80, y: 52 },
      { x: 52, y: 52 },
      { x: 52, y: 80 },
      { x: 28, y: 80 },
      { x: 28, y: 52 },
      { x: 0, y: 52 },
      { x: 0, y: 28 },
      { x: 28, y: 28 },
    ],
  },
  chevron: {
    fabricType: 'polygon',
    width: 100,
    height: 100,
    points: [
      { x: 0, y: 50 },
      { x: 45, y: 10 },
      { x: 45, y: 35 },
      { x: 100, y: 35 },
      { x: 100, y: 65 },
      { x: 45, y: 65 },
      { x: 45, y: 90 },
    ],
  },
  lightning: {
    fabricType: 'polygon',
    width: 70,
    height: 93,
    points: [
      { x: 45, y: 5 },
      { x: 20, y: 45 },
      { x: 35, y: 45 },
      { x: 12, y: 88 },
      { x: 58, y: 38 },
      { x: 40, y: 38 },
    ],
  },
  tear: {
    fabricType: 'polygon',
    width: 100,
    height: 100,
    points: [
      { x: 50, y: 0 },
      { x: 100, y: 40 },
      { x: 50, y: 100 },
      { x: 0, y: 40 },
    ],
  },
  heart: {
    fabricType: 'polygon',
    width: 100,
    height: 90,
    points: [
      { x: 50, y: 85 },
      { x: 10, y: 50 },
      { x: 0, y: 20 },
      { x: 25, y: 0 },
      { x: 50, y: 15 },
      { x: 75, y: 0 },
      { x: 100, y: 20 },
      { x: 90, y: 50 },
    ],
  },
  semicircle: {
    fabricType: 'polygon',
    width: 80,
    height: 40,
    points: [
      { x: 0, y: 40 },
      { x: 0, y: 20 },
      { x: 10, y: 5 },
      { x: 40, y: 0 },
      { x: 70, y: 5 },
      { x: 80, y: 20 },
      { x: 80, y: 40 },
    ],
  },
  blob: {
    fabricType: 'polygon',
    width: 80,
    height: 80,
    points: [
      { x: 50, y: 10 },
      { x: 90, y: 10 },
      { x: 90, y: 50 },
      { x: 90, y: 90 },
      { x: 50, y: 90 },
      { x: 10, y: 90 },
      { x: 10, y: 50 },
      { x: 10, y: 10 },
    ],
  },
  crescent: { fabricType: 'circle', width: 100, height: 100, radius: 50 },
  ring: {
    fabricType: 'circle',
    width: 100,
    height: 100,
    radius: 50,
    fill: 'transparent',
    strokeWidth: 14,
  },
  wave: {
    fabricType: 'polyline',
    width: 100,
    height: 40,
    closed: false,
    fill: 'transparent',
    strokeWidth: 4,
    points: [
      { x: 0, y: 40 },
      { x: 25, y: 0 },
      { x: 50, y: 40 },
      { x: 75, y: 0 },
      { x: 100, y: 40 },
    ],
  },
  zigzag: {
    fabricType: 'polyline',
    width: 144,
    height: 28,
    closed: false,
    fill: 'transparent',
    strokeWidth: 4,
    points: [
      { x: 0, y: 24 },
      { x: 24, y: 0 },
      { x: 48, y: 24 },
      { x: 72, y: 0 },
      { x: 96, y: 24 },
      { x: 120, y: 0 },
      { x: 144, y: 24 },
    ],
  },
  arrow: {
    fabricType: 'polygon',
    width: 160,
    height: 24,
    fill: 'transparent',
    strokeWidth: 3,
    points: [
      { x: 0, y: 10 },
      { x: 140, y: 10 },
      { x: 140, y: 0 },
      { x: 160, y: 12 },
      { x: 140, y: 24 },
      { x: 140, y: 14 },
      { x: 0, y: 14 },
    ],
  },
  doubleArrow: {
    fabricType: 'line',
    width: 200,
    height: 20,
    strokeWidth: 3,
    fill: 'transparent',
  },
  cloud: {
    fabricType: 'polygon',
    width: 120,
    height: 72,
    points: [
      { x: 25, y: 60 },
      { x: 8, y: 45 },
      { x: 24, y: 32 },
      { x: 55, y: 22 },
      { x: 92, y: 32 },
      { x: 108, y: 52 },
      { x: 85, y: 62 },
    ],
  },
  speechBubble: {
    fabricType: 'polygon',
    width: 116,
    height: 82,
    points: [
      { x: 14, y: 10 },
      { x: 108, y: 10 },
      { x: 108, y: 54 },
      { x: 50, y: 54 },
      { x: 32, y: 82 },
      { x: 40, y: 54 },
      { x: 14, y: 54 },
    ],
  },
};

export function getKonvaShapeSpec(shapeKey) {
  return KONVA_SHAPE_SPECS[shapeKey] ? { ...KONVA_SHAPE_SPECS[shapeKey] } : null;
}

export function konvaShapeSpecAt(shapeKey, left, top) {
  const base = getKonvaShapeSpec(shapeKey) || getKonvaShapeSpec('rect');
  const w = base.width || 120;
  const h = base.height || 80;
  return {
    ...base,
    left: left - w / 2,
    top: top - h / 2,
  };
}
